import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import portalApi, { apiError } from './portalApi';
import { T } from './portalTheme';
import { AuthLayout, AuthHeading, Field, TextInput, Button, Alert, Icon, IC } from './PortalWidgets';

// /questionnaire/portal/forgot-password -- the backend answers identically whether or not the
// email has an account, so this page always shows the same "check your inbox" confirmation.
export default function PortalForgotPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState(params.get('email') || '');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await portalApi.post('/v1/questionnaire-responses/portal/forgot-password', { email });
      setSent(true);
    } catch (err) {
      setError(apiError(err, 'Could not send the reset email. Try again in a few minutes.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <AuthLayout>
        <div style={{ textAlign: 'center' }}>
          <span style={{ width: 56, height: 56, borderRadius: 16, background: T.blueBg, color: T.primary, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
            <Icon d={IC.mail} size={26} />
          </span>
          <div style={{ fontSize: 24, fontWeight: 800, color: T.ink, letterSpacing: -0.3 }}>Check your inbox</div>
          <div style={{ fontSize: 14, color: T.muted, marginTop: 10, lineHeight: 1.6 }}>
            If an account exists for <strong style={{ color: T.ink }}>{email}</strong>, we've sent a link to reset its password.
            The link works once and expires in one hour.
          </div>
          <div style={{ marginTop: 28 }}>
            <Button variant="secondary" full icon={IC.back} onClick={() => navigate('/questionnaire/portal/login')}>Back to sign in</Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthHeading title="Reset your password" subtitle="Enter the email you use for the vendor portal and we'll send you a link to choose a new password." />
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field label="Work email">
          <TextInput value={email} onChange={e => setEmail(e.target.value)} type="email" autoComplete="email" required autoFocus placeholder="you@company.com" />
        </Field>
        {error && <Alert tone="error">{error}</Alert>}
        <Button type="submit" size="lg" full loading={submitting}>{submitting ? 'Sending…' : 'Send reset link'}</Button>
      </form>
      <div style={{ fontSize: 13.5, color: T.muted, marginTop: 24, textAlign: 'center' }}>
        Remembered it?{' '}
        <Link to="/questionnaire/portal/login" className="pw-link" style={{ color: T.primary, fontWeight: 600, textDecoration: 'none' }}>Back to sign in</Link>
      </div>
    </AuthLayout>
  );
}
